import { SiteUrls } from '@tableau/shared-urls';
import { DataGrid } from '@tableau/widgets-datagrid';
import * as _ from 'underscore';

import { appStore } from '../../app/AppStore';
import { RunFlowTasksGrid } from '../../app/content/RunFlowTasksGrid';
import { SessionServerSelectors } from '../../app/selectors/SessionInfoSelectors';
import { ServerUrls } from '../../app/ServerUrls';
import { IActionTarget } from '../../common/react/components/ActionsMenu';
import { AngularToReactDataGridBridge } from '../../common/react/components/dataGrid/AngularToReactDataGridBridge';
import { FeatureFlags } from '../FeatureFlags';
import { IScheduleFrequencyDescriptionFilter } from '../filters/ScheduleFrequencyDescriptionFilter';
import { ActionButtonsHelper, IActionsMenuScope, SubplaceWithActionButtonsScope } from '../helpers/ActionButtonsHelper';
import { IActionContext } from '../helpers/ActionContext';
import { SchedulingErrorCode } from '../helpers/ErrorCodes';
import { SubplaceCountsHelper, SubplaceEvents } from '../helpers/SubplaceCountsHelper';
import { RunFlowTaskContentType } from '../models/ContentTypes';
import { IEmptyTemplateModel } from '../models/IEmptyTemplate';
import { ISubplace } from '../models/IPlace';
import { RunFlowTaskInfo } from '../models/RunFlowTaskInfo';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { ResourceQuery } from '../resources/ResourceQuery';
import { IResult } from '../ServerApi/types/IResponse';
import { ActionHelpers } from '../services/ActionHelpers';
import { IActionButton } from '../services/ActionPanelService';
import { BrowserTitleService } from '../services/BrowserTitleService';
import { ChangeRunFlowTasksPriorityAction } from '../services/ChangeRunFlowTasksPriorityAction';
import { ChangeScheduleAction } from '../services/ChangeScheduleAction';
import { DeleteRunFlowTasksAction } from '../services/DeleteRunFlowTasksAction';
import { ErrorState } from '../services/ErrorState';
import { IQueryUpdater, QueryService } from '../services/QueryService';
import { ResumeRunFlowTasksAction } from '../services/ResumeRunFlowTasksAction';
import { RunFlowTasksAction } from '../services/RunFlowTasksAction';
import { ServerService } from '../services/ServerService';
import { SorterFactory } from '../services/SorterFactory';
import { ListActionsScope, SubplaceWithDataGridScope } from './ContentSubplaceCtrl';
import { ISchedulePlaceScope } from './SchedulePlaceCtrl';
import { ITasksScope } from './TasksCtrl';

export interface IFlowRunsScope extends
  ITasksScope,
  IActionsMenuScope<RunFlowTaskInfo, {}>,
  ListActionsScope,
  SubplaceWithActionButtonsScope<RunFlowTaskInfo, {}>,
  SubplaceWithDataGridScope<RunFlowTaskInfo> {}

export interface IFlowRunsContext {
  createFlowTaskFn: (context: IActionContext) => ng.IPromise<any>;
  modifyFlowTasksPermission: boolean;
}

export class RunFlowTasksCtrl {

  public static $inject = [
    '$filter',
    '$q',
    '$scope',
    '$state',
    '$translate',
    'AngularToReactDataGridBridge',
    'BrowserTitleService',
    'ChangeRunFlowTasksPriorityAction',
    'ChangeScheduleAction',
    'DeleteRunFlowTasksAction',
    'ErrorState',
    'QueryService',
    'ResumeRunFlowTasksAction',
    'RunFlowTasksAction',
    'ServerService',
    'SorterFactory',
    'subplace',
  ];

  private query: IQueryUpdater;
  private waitForCountOfSchedules: ng.IDeferred<number>;

  constructor(
    private $filter: ng.IFilterService,
    private $q: ng.IQService,
    private $scope: IFlowRunsScope,
    private $state: ng.ui.IStateService,
    private $translate: ng.translate.ITranslateService,
    private AngularToReactDataGridBridge: AngularToReactDataGridBridge,
    BrowserTitleService: BrowserTitleService,
    private ChangeRunFlowTasksPriorityAction: ChangeRunFlowTasksPriorityAction,
    private ChangeScheduleAction: ChangeScheduleAction,
    private DeleteRunFlowTasksAction: DeleteRunFlowTasksAction,
    ErrorState: ErrorState,
    queryService: QueryService,
    private ResumeRunFlowTasksAction: ResumeRunFlowTasksAction,
    private RunFlowTasksAction: RunFlowTasksAction,
    private ServerService: ServerService,
    sorterFactory: SorterFactory,
    subplace: ISubplace<RunFlowTaskInfo>,
  ) {
    if (!((!this.isServerPage() && ServerService.flowsOnSiteEnabled()) || (this.isServerPage() && ServerService.flowsOnServerEnabled()))) {
      ErrorState.go({
        detailsLocKey: 'pageDoesNotExist',
        redirectState: 'main.site.content.site.projects',
        redirectLocKey: 'backToContent'
      });
      return;
    }

    BrowserTitleService.setTitleWithPlaceEntityNameAndSubplaceName({placeEntityName: $scope.objectName, translatableSubplaceName: 'flows'});

    this.waitForCountOfSchedules = $q.defer();
    $scope.selectedItems = new Set<RunFlowTaskInfo>((flowRun: RunFlowTaskInfo) => flowRun.id);

    const actions = this.createActions($scope.selectedItems);
    const getActionTarget = ActionButtonsHelper.getActionTargetFn($scope, $scope.selectedItems);
    $scope.anyActionsAllowed = () => true; // TODO permissions

    this.configureForReactDataGrid(actions, getActionTarget);

    $scope.sorter = sorterFactory.create(RunFlowTaskContentType.defaultOrder);

    this.query = queryService.createQueryUpdaterFromSubplace(
      $scope,
      subplace,
      (resourceQuery: ResourceQuery<RunFlowTaskInfo>) => {
        this.AngularToReactDataGridBridge.updateReactDataGridRowStore({ scope: $scope, sliceable: resourceQuery });
        $scope.sliceable = resourceQuery;
        $scope.sorter.setOrder(resourceQuery.getOrder());
      });
    this.refreshRunFlowTasks();

    $scope.$on(SubplaceEvents.Refresh, () => {
      this.refreshRunFlowTasks();
    });

    ActionButtonsHelper.setupScopeForActionsMenuInActionBar({
      scope: $scope,
      isActionAvailable: action => action.isAvailable($scope.selectedItems),
      menuActions: actions,
      getActionTarget,
      translate: this.$translate.instant.bind(this.$translate)
    });

    //// Respond to grid data updates
    // React data grid
    if ($scope.dataGridProps) {
      $scope.dataGridProps.updated = (rows: DataGrid.Row[]) => $scope.$apply(() => {
        this.waitForCountOfSchedules.resolve(rows.length);
        $scope.selectedItems.updateItems(_.map(_.compact(rows), (row) => row.data));
        SubplaceCountsHelper.updateCountsFromSubplace($scope, subplace, rows.length);
      });
    }

    $scope.emptyTemplateModel = this.createEmptyTemplateModel();
  }

  private isServerPage() {
    return this.$state.includes('main.server.**');
  }

  private refreshRunFlowTasks() {
    this.waitForCountOfSchedules = this.$q.defer();
    this.query.refresh();
  }

  private createEmptyTemplateModel() {
    const emptyTemplateModel = <IEmptyTemplateModel> {};
    emptyTemplateModel.title = 'runFlowTasksEmptyMessage';
    return emptyTemplateModel;
  }

  private showScheduleColumn() {
    return !this.$state.includes('**.schedule.**');
  }

  private showSiteColumn() {
    return this.isServerPage();
  }

  private showPriorityColumn() {
    return !this.ServerService.ignoreExtractTaskPriority(); // TODO rename ignoreExtractTaskPriority to be more generic
  }

  private showStatusColumn() {
    return FeatureFlags.autoSuspendFlowTasksEnabled;
  }

  private getFlowFailureThresholdForRunPrevention(): number {
    const flowFailureThresholdForRunPrevention = SessionServerSelectors.flowFailureThresholdForRunPrevention(appStore.getState());
    if (flowFailureThresholdForRunPrevention && flowFailureThresholdForRunPrevention > 0) {
      return flowFailureThresholdForRunPrevention;
    } else {
      return -1;
    }
  }

  private configureForReactDataGrid(
      actions: IActionButton<RunFlowTaskInfo, {}>[],
      getActionTarget: () => IActionTarget<RunFlowTaskInfo, {}>
    ) {
    const siteUrls = new SiteUrls({
      siteUrlName: this.ServerService.sessionSiteUrlName(),
      queryString: window.location.search
    });

    const columns = RunFlowTasksGrid.columns(
      this.$translate.instant.bind(this.$translate),
      this.$scope,
      true, // TODO permissions for param anyActionsAllowed
      actions,
      getActionTarget,
      this.isServerPage(),
      ServerUrls.getInstance(),
      siteUrls,
      <IScheduleFrequencyDescriptionFilter>this.$filter('scheduleFrequencyDescription'),
      true,
      this.showScheduleColumn(),
      this.showSiteColumn(),
      this.showPriorityColumn(),
      this.showStatusColumn(),
      this.ServerService.isSessionSiteOrServerAdmin(),
      this.getFlowFailureThresholdForRunPrevention(),
    );

    this.AngularToReactDataGridBridge.attachReactDataGridToScope({
      $scope: this.$scope,
      columns,
      emptyChild: this.AngularToReactDataGridBridge.emptyTemplateElementFor(this.createEmptyTemplateModel()),
      canShowFilterPanel: false
    });
  }

  private createActions(selectedItems: Set<RunFlowTaskInfo>): IActionButton<RunFlowTaskInfo, {}>[] {
    return [{
        name: 'tasks-run-now',
        translate: 'runNow_action',
        isAvailable: (runFlowTasks: Set<RunFlowTaskInfo>) => !runFlowTasks.isEmpty(),
        execute: (items: Set<RunFlowTaskInfo>, context: IActionContext) => {
          this.RunFlowTasksAction.execute(items, context)
            .then((result: IResult) => {
              ActionHelpers.deselectNotFound(selectedItems, result, SchedulingErrorCode.SchedulingTaskNotFound);
              this.refreshRunFlowTasks();
            });
          }
      },
      {
        name: 'tasks-change-schedule',
        translate: 'changeSchedule_action',
        isAvailable: (runFlowTasks: Set<RunFlowTaskInfo>) =>!runFlowTasks.isEmpty(),
        execute: (items: Set<RunFlowTaskInfo>, context: IActionContext) => {
          this.ChangeScheduleAction.forRunFlowTasks(items, context).then((result: IResult) => {
              const scheduleScope = <ISchedulePlaceScope><any>this.$scope;
              if (scheduleScope.schedule) {
                ActionHelpers.deselectAllExceptErrors(selectedItems, result, SchedulingErrorCode.SchedulingTaskNotFound);
              } else {
                ActionHelpers.deselectNotFound(selectedItems, result, SchedulingErrorCode.SchedulingTaskNotFound);
              }
              this.refreshRunFlowTasks();
            });
        }
      }, {
        name: 'tasks-priority',
        translate: 'tasksSetPriority_action',
        isAvailable: (runFlowTasks: Set<RunFlowTaskInfo>) =>
          !this.ServerService.ignoreExtractTaskPriority() && !runFlowTasks.isEmpty(),
        execute: (items: Set<RunFlowTaskInfo>, context: IActionContext) => {
          this.ChangeRunFlowTasksPriorityAction.execute(items, context)
            .then((result: IResult) => {
              ActionHelpers.deselectNotFound(selectedItems, result, SchedulingErrorCode.SchedulingTaskNotFound);
              this.refreshRunFlowTasks();
            });
        }
      }, {
        name: 'tasks-resume',
        translate: 'runFlowTasksResumeAction',
        isAvailable: (runFlowTasks: Set<RunFlowTaskInfo>) => FeatureFlags.autoSuspendFlowTasksEnabled
          && !runFlowTasks.isEmpty(),
        isDisabled: (runFlowTasks: Set<RunFlowTaskInfo>) => runFlowTasks.size() === 1 && !runFlowTasks.first().suspended,
        execute: (items: Set<RunFlowTaskInfo>, context: IActionContext) => {
          this.ResumeRunFlowTasksAction.executeWithContext(items, context)
            .then((result: IResult) => {
              ActionHelpers.deselectAllExceptErrors(selectedItems, result, SchedulingErrorCode.SchedulingTaskNotFound);
              this.refreshRunFlowTasks();
            });
        }
      }, {
        name: 'tasks-delete',
        translate: 'delete_action',
        isAvailable: (runFlowTasks: Set<RunFlowTaskInfo>) => !runFlowTasks.isEmpty(),
        execute: (items: Set<RunFlowTaskInfo>, context: IActionContext) => {
          this.DeleteRunFlowTasksAction.executeWithContext(items, context)
            .then((result: IResult) => {
              ActionHelpers.deselectAllExceptErrors(selectedItems, result, SchedulingErrorCode.SchedulingTaskNotFound);
              this.refreshRunFlowTasks();
            });
        }
      }];
  }
}

Module.instance().controller('RunFlowTasksCtrl', RunFlowTasksCtrl);
